(function () {
  'use strict';

  const ROOT = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  const ADDON_TYPES = {
    paper: true,
    personalization: true,
    'elastic-addon': true,
    pairs: true,
    'spare-elastic': true
  };

  const fetchJSON = async (url, opts = {}) => {
    const headers = { Accept: 'application/json', ...(opts.headers || {}) };
    if (opts.body && !headers['Content-Type']) headers['Content-Type'] = 'application/json';
    const res = await fetch(url, {
      credentials: 'same-origin',
      ...opts,
      headers
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  };

  const normalize = (s) =>
    (s || '')
      .toString()
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .trim();

  const getProp = (item, propName) => {
    if (!item || !item.properties) return '';
    const p = item.properties;
    const target = normalize(propName);

    if (Array.isArray(p)) {
      for (const entry of p) {
        const key = normalize(entry.name || entry.key || entry.first || '');
        if (key === target) return (entry.value ?? entry.last ?? '').toString().trim();
      }
      return '';
    }

    if (typeof p === 'object') {
      for (const [k, v] of Object.entries(p)) {
        if (normalize(k) === target) return (v ?? '').toString().trim();
      }
    }

    return '';
  };

  let currentCartItems = [];
  let syncing = false;
  let syncTimer = 0;

  const setCartItems = (items) => {
    currentCartItems = items || [];
  };

  const updateCart = async () => {
    const cart = await fetchJSON(`${ROOT}cart.js`);
    setCartItems(cart.items);
    return cart;
  };

  const refreshUpcart = () => {
    if (typeof window.upcartRefreshCart === 'function') window.upcartRefreshCart();
  };

  const applyUpdates = async (updates, extra = {}) => {
    const keys = Object.keys(updates);
    if (!keys.length) return null;
    const cart = await fetchJSON(`${ROOT}cart/update.js`, {
      method: 'POST',
      body: JSON.stringify({ updates, ...extra })
    });
    setCartItems(cart.items);
    refreshUpcart();
    return cart;
  };

  const getForProductValue = (item) => getProp(item, 'For Product') || null;

  const getBundleToken = (item) => getProp(item, '_bundle_token') || '';

  const getMainTitle = (item) => {
    if (!item) return '';
    const base = item.product_title || item.untranslated_product_title || item.title || '';
    const vt = item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '';
    return vt ? `${base} - ${vt}` : base;
  };

  const isPaperProductType = (item) => {
    const pt = normalize(item?.product_type);
    return (
      pt === 'paper' ||
      pt.includes('paper refill') ||
      pt.includes('notebook refill') ||
      pt.startsWith('paper ')
    );
  };

  const hasPaperLinkMarkers = (item) =>
    !!(getBundleToken(item) || getForProductValue(item));

  const isPairsChild = (item) =>
    normalize(getProp(item, '_linked_child')) === 'pairs' || !!getProp(item, '_asp_pairs_well');

  const isAddonType = (item) => {
    if (ADDON_TYPES[normalize(item?.product_type)]) return true;
    if (ADDON_TYPES[normalize(getProp(item, '_linked_child'))]) return true;
    if (isPairsChild(item)) return true;
    return isPaperProductType(item) && hasPaperLinkMarkers(item);
  };

  const isPaperChild = (item) => {
    if (normalize(getProp(item, '_linked_child')) === 'paper') return true;
    return isPaperProductType(item) && hasPaperLinkMarkers(item);
  };

  const isPersonalizationChild = (item) =>
    normalize(getProp(item, '_linked_child')) === 'personalization' ||
    normalize(item?.product_type) === 'personalization';

  const isSpareElasticChild = (item) => normalize(getProp(item, '_linked_child')) === 'spare-elastic';

  const skipsQtySync = (item) =>
    isPaperChild(item) || isPersonalizationChild(item) || isPairsChild(item) || isSpareElasticChild(item);

  const isMainProduct = (item) => !isAddonType(item);

  const isChildOf = (child, main) => {
    if (!child || !main || (main.key && child.key === main.key)) return false;
    if (!isAddonType(child)) return false;
    const childTok = getBundleToken(child);
    const mainTok = getBundleToken(main);
    if (childTok) return !!mainTok && childTok === mainTok;
    const fp = getForProductValue(child);
    return !!(fp && normalize(fp) === normalize(getMainTitle(main)));
  };

  const findChildKeys = (items, mainItem) =>
    (items || []).filter((it) => isChildOf(it, mainItem)).map((it) => it.key).filter(Boolean);

  const prepareBundledSections = () => {
    const sectionsToBundle = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', {
        bubbles: true,
        detail: { sections: sectionsToBundle }
      })
    );
    return sectionsToBundle;
  };

  const applyBundledUpdateByKey = async (mainKey, targetQty) => {
    if (syncing) return null;
    if (!currentCartItems.length) await updateCart();

    const mainItem = currentCartItems.find((i) => i.key === mainKey);
    if (!mainItem || !isMainProduct(mainItem)) return null;

    const updates = { [mainKey]: targetQty };
    findChildKeys(currentCartItems, mainItem).forEach((k) => {
      const child = currentCartItems.find((i) => i.key === k);
      if (targetQty > 0 && child && skipsQtySync(child)) return;
      updates[k] = targetQty;
    });

    syncing = true;
    try {
      const cart = await applyUpdates(updates, { sections: prepareBundledSections().join(',') });
      if (cart) {
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:change', {
            bubbles: true,
            detail: { baseEvent: 'line-item:change', cart }
          })
        );
      }
      return cart;
    } finally {
      syncing = false;
    }
  };

  const removeLinkedChildren = async (removedMain) => {
    if (syncing || !removedMain) return;
    if (!currentCartItems.length) await updateCart();

    const main =
      (removedMain.key && currentCartItems.find((i) => i.key === removedMain.key)) || removedMain;
    if (!isMainProduct(main)) return;

    const childKeys = findChildKeys(currentCartItems, main);
    if (!childKeys.length) return;

    syncing = true;
    try {
      const updates = {};
      childKeys.forEach((k) => {
        updates[k] = 0;
      });
      await applyUpdates(updates);
    } finally {
      syncing = false;
    }
  };

  const syncBundledQuantities = async (forceRefresh) => {
    if (syncing) return;
    if (forceRefresh || !currentCartItems.length) await updateCart();

    const items = currentCartItems;
    const mains = items.filter(isMainProduct);
    const updates = {};

    items.forEach((it) => {
      if (!isAddonType(it) || !it.key) return;
      const parent = mains.find((m) => isChildOf(it, m));
      if (!parent) {
        updates[it.key] = 0;
        return;
      }
      if (skipsQtySync(it)) return;
      if (it.quantity !== parent.quantity) updates[it.key] = parent.quantity;
    });

    if (!Object.keys(updates).length) return;

    syncing = true;
    try {
      await applyUpdates(updates);
    } finally {
      syncing = false;
    }
  };

  const scheduleSync = (forceRefresh) => {
    if (Date.now() < (Number(window.__aspCartQuietUntil) || 0)) return;
    window.clearTimeout(syncTimer);
    const run = () => {
      if (Date.now() < (Number(window.__aspCartQuietUntil) || 0)) return;
      if (syncing) {
        syncTimer = window.setTimeout(run, 40);
        return;
      }
      syncBundledQuantities(forceRefresh).catch(() => {});
    };
    syncTimer = window.setTimeout(run, 50);
  };

  updateCart();

  const isCartPage = () => {
    try {
      if (window.themeVariables && themeVariables.settings && themeVariables.settings.pageType === 'cart') {
        return true;
      }
    } catch (e) {}
    return /(^|\/)cart\/?$/.test(window.location.pathname || '');
  };

  document.addEventListener('cart:change', (event) => {
    if (event?.detail?.cart?.items) setCartItems(event.detail.cart.items);
  });

  document.addEventListener('cart:refresh', updateCart);

  document.addEventListener(
    'click',
    function (event) {
      const link = event.target.closest('a[href*="/cart/change"]');
      if (!link) return;

      const url = new URL(link.href, window.location.origin);
      let key = url.searchParams.get('id');
      const qty = Number(url.searchParams.get('quantity'));
      const lineParam = url.searchParams.get('line');

      if (!key && lineParam) {
        const lineIndex = Number(lineParam) - 1;
        if (lineIndex >= 0 && currentCartItems[lineIndex]) {
          key = currentCartItems[lineIndex].key;
        }
      }

      if (!key || Number.isNaN(qty)) return;

      const mainItem = currentCartItems.find((i) => i.key === key);
      if (!mainItem || !isMainProduct(mainItem)) return;

      if (qty === 0) {
        event.preventDefault();
        event.stopPropagation();
        applyBundledUpdateByKey(key, 0)
          .then((cart) => {
            if (isCartPage()) {
              window.location.reload();
              return;
            }
            if (!cart) window.location.href = link.href;
          })
          .catch(() => {
            window.location.href = link.href;
          });
        return;
      }

      if (!isCartPage()) return;

      const childKeys = findChildKeys(currentCartItems, mainItem).filter((k) => {
        const child = currentCartItems.find((i) => i.key === k);
        return child && !skipsQtySync(child);
      });
      if (!childKeys.length) return;

      event.preventDefault();
      event.stopPropagation();
      applyBundledUpdateByKey(key, qty)
        .then(() => {
          window.location.reload();
        })
        .catch(() => {
          window.location.href = link.href;
        });
    },
    true
  );

  document.addEventListener(
    'line-item:will-change',
    function (event) {
      const qty = event?.detail?.targetQuantity;
      if (typeof qty !== 'number' || qty !== 0) return;

      let key = null;
      const host = event.target.closest('[data-line-key], line-item-quantity, line-item');
      if (host) {
        key = host.getAttribute('data-line-key');
        if (!key) {
          const keyEl = host.querySelector('[data-line-key]');
          key = keyEl ? keyEl.getAttribute('data-line-key') : null;
        }
      }
      if (!key) return;

      const mainItem = currentCartItems.find((i) => i.key === key);
      if (!mainItem || !isMainProduct(mainItem)) return;

      event.preventDefault();
      event.stopPropagation();

      applyBundledUpdateByKey(key, 0)
        .then((cart) => {
          if (isCartPage()) window.location.reload();
          else if (!cart) {
          }
        })
        .catch(() => {});
    },
    true
  );

  document.addEventListener('cart:change', function (event) {
    if (syncing) return;
    if (event?.detail?.baseEvent !== 'line-item:change') return;
    scheduleSync();
  });

  if (isCartPage()) {
    updateCart()
      .then(() => scheduleSync(true))
      .catch(() => {});
  }

  const registerUpcartHooks = () => {
    if (window.__aspLinkedAddonsUpcartBound) return true;
    if (!window.upcartSubscribeItemRemoved || !window.upcartSubscribeCartUpdated) return false;

    window.__aspLinkedAddonsUpcartBound = true;

    window.upcartSubscribeItemRemoved((event) => {
      removeLinkedChildren(event?.item).catch(() => {});
    });

    window.upcartSubscribeCartUpdated(() => {
      scheduleSync(true);
    });

    return true;
  };

  registerUpcartHooks();
  const upcartTimer = window.setInterval(() => {
    if (registerUpcartHooks()) window.clearInterval(upcartTimer);
  }, 400);
  window.setTimeout(() => window.clearInterval(upcartTimer), 20000);
})();
